import { pgSelect, pgInsert, pgDelete, isSupabaseConfigured } from './supabaseClient';
import { logEvent } from './debugLog';
import { loadConfig } from './configService';

/**
 * Multi-stop tours: ordered POI sequences (museum highlights, onboarding
 * walks). Stored in the `tours` table; the visitor flow routes stop-by-stop
 * with auto-advance on arrival (see main.ts tour state).
 *
 * Scoped by map_code (2026-07-21, same fix as poiService.ts — see its
 * comment for why loadConfig().code is resolved internally here rather
 * than threaded through every call site).
 */

export interface Tour {
  id: string;
  name: string;
  description?: string;
  poiIds: string[];
}

interface TourRow {
  id: string;
  name: string;
  description: string | null;
  poi_ids: string[];
}

let cache: Tour[] | null = null;
let cacheMapCode: string | null = null;

function offlineKey(mapCode: string): string {
  return `navme.cache.tours.${mapCode || 'default'}`;
}

export async function getTours(forceRefresh = false): Promise<Tour[]> {
  const mapCode = loadConfig().code;
  if (cache && !forceRefresh && cacheMapCode === mapCode) return cache;
  if (!isSupabaseConfigured()) return [];
  try {
    const rows = await pgSelect<TourRow>('tours', `select=*&map_code=eq.${encodeURIComponent(mapCode)}&order=name`);
    cache = rows.map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description ?? undefined,
      poiIds: r.poi_ids ?? [],
    }));
    cacheMapCode = mapCode;
    try {
      localStorage.setItem(offlineKey(mapCode), JSON.stringify(cache));
    } catch {
      /* quota */
    }
    return cache;
  } catch (err) {
    logEvent('error', `getTours failed — trying offline snapshot: ${err instanceof Error ? err.message : String(err)}`);
    try {
      const snap = JSON.parse(localStorage.getItem(offlineKey(mapCode)) ?? 'null') as Tour[] | null;
      if (snap) return snap;
    } catch {
      /* corrupt snapshot */
    }
    return [];
  }
}

export async function createTour(name: string, description: string | undefined, poiIds: string[]): Promise<void> {
  const id = `tour-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'tour'}-${Date.now().toString(36)}`;
  await pgInsert('tours', { id, name, description: description ?? null, poi_ids: poiIds, map_code: loadConfig().code });
  cache = null;
  cacheMapCode = null;
  logEvent('admin', `tour created "${name}" (${poiIds.length} stops)`);
}

export async function deleteTour(id: string): Promise<void> {
  await pgDelete('tours', id);
  cache = null;
  logEvent('admin', `tour deleted ${id}`);
}
